import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Maximize2, Minimize2, MousePointer2, Undo2, X, ZoomIn, ZoomOut } from "lucide-react";

const centroid = (pts) => ({
  x: pts.reduce((a, p) => a + p.x, 0) / pts.length,
  y: pts.reduce((a, p) => a + p.y, 0) / pts.length,
});

const stripTags = (h) => (h || "").replace(/<[^>]+>/g, " ").trim();
const LONG = 260;

export default function ImageMap({ data }) {
  const [active, setActive] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(null);
  const [layer, setLayer] = useState(null); // null = calque de base (id 0)
  const [fs, setFs] = useState(false); // plein écran
  const [view, setView] = useState({ scale: 1, x: 0, y: 0 }); // zoom/pan tactile
  const ptrs = useRef(new Map());
  const gesture = useRef(null);
  const moved = useRef(false);
  const closeTimer = useRef(null);
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => {
      setActive(null);
      setExpanded(false);
    }, 220); // pont souris zone -> infobulle : évite la fermeture en traversant l'espace
  };
  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const isTouch = useMemo(
    () => typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches,
    []
  );
  const navigate = useNavigate();

  const clampView = (v) => {
    const scale = Math.min(Math.max(v.scale, 1), 5);
    if (scale === 1) return { scale, x: 0, y: 0 };
    const lim = (scale - 1) * 500;
    return {
      scale,
      x: Math.min(Math.max(v.x, -lim), lim),
      y: Math.min(Math.max(v.y, -lim), lim),
    };
  };

  const onPtrDown = (e) => {
    ptrs.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    moved.current = false;
    if (ptrs.current.size === 2) {
      const [a, b] = [...ptrs.current.values()];
      gesture.current = {
        mode: "pinch",
        dist: Math.hypot(a.x - b.x, a.y - b.y) || 1,
        scale: view.scale,
        x: view.x,
        y: view.y,
        cx: (a.x + b.x) / 2,
        cy: (a.y + b.y) / 2,
      };
    } else {
      gesture.current = { mode: "pan", sx: e.clientX, sy: e.clientY, x: view.x, y: view.y };
    }
  };

  const onPtrMove = (e) => {
    if (!ptrs.current.has(e.pointerId)) return;
    ptrs.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const g = gesture.current;
    if (!g) return;
    // capturer seulement une fois le geste engagé : sinon le clic est redirigé vers le cadre
    const engage = () => {
      if (!moved.current) {
        moved.current = true;
        try {
          e.currentTarget.setPointerCapture?.(e.pointerId);
        } catch {}
      }
    };
    if (g.mode === "pinch" && ptrs.current.size >= 2) {
      const [a, b] = [...ptrs.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y) || 1;
      const cx = (a.x + b.x) / 2;
      const cy = (a.y + b.y) / 2;
      engage();
      setView((v) =>
        clampView({ scale: g.scale * (dist / g.dist), x: g.x + (cx - g.cx), y: g.y + (cy - g.cy) })
      );
    } else if (g.mode === "pan") {
      const dx = e.clientX - g.sx;
      const dy = e.clientY - g.sy;
      if (Math.abs(dx) + Math.abs(dy) > 8) engage();
      setView((v) => (v.scale > 1 ? clampView({ ...v, x: g.x + dx, y: g.y + dy }) : v));
    }
  };

  const onPtrUp = (e) => {
    ptrs.current.delete(e.pointerId);
    if (ptrs.current.size === 0) gesture.current = null;
    else if (ptrs.current.size < 2 && gesture.current?.mode === "pinch") gesture.current = null;
  };

  const toggleFs = () => {
    const next = !fs;
    setFs(next);
    if (next && isTouch) setView({ scale: 1.6, x: 0, y: 0 }); // pré-zoom pour lecture au doigt
    if (!next) {
      setView({ scale: 1, x: 0, y: 0 });
      setActive(null);
      setExpanded(false);
    }
  };

  useEffect(() => {
    if (!fs) return;
    const onKey = (e) => {
      if (e.key === "Escape") setFs(false);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [fs]);

  const layers = data?.layers || [];
  const hasLayers = layers.length > 0;
  const baseLayerId = hasLayers ? (layers.find((l) => l.id === 0)?.id ?? layers[0].id) : 0;
  const currentLayer = layer ?? baseLayerId;
  const currentImage = hasLayers
    ? (layers.find((l) => l.id === currentLayer)?.image || data.image)
    : data.image;
  const currentTitle = hasLayers
    ? (layers.find((l) => l.id === currentLayer)?.title || "")
    : "";

  const spots = useMemo(
    () =>
      (data?.spots || []).filter(
        (s) =>
          s.points &&
          s.points.length >= 3 &&
          (!hasLayers || (s.layer ?? 0) === currentLayer)
      ),
    [data, hasLayers, currentLayer]
  );
  if (!currentImage || !(data?.spots || []).length) return null;

  const activeSpot = spots.find((s) => s.id === active);
  const tip = activeSpot ? centroid(activeSpot.points) : null;
  const tipHtml = activeSpot
    ? (activeSpot.tooltip_html || "").replace(
        new RegExp(`^<h4>\\s*${activeSpot.title.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s*</h4>`, "i"),
        ""
      )
    : "";
  const isLong = stripTags(tipHtml).length > LONG;
  const showFull = expanded && activeSpot;

  const tipStyle = tip
    ? {
        left: `${Math.min(Math.max(tip.x, 18), 82)}%`,
        top: `${tip.y}%`,
        transform:
          tip.y > 30 ? "translate(-50%, calc(-100% - 14px))" : "translate(-50%, 14px)",
      }
    : {};

  const openSpot = (id) => {
    setActive(id);
    setExpanded(false);
  };

  const gotoLayer = (id) => {
    setLayer(id);
    setActive(null);
    setExpanded(false);
  };

  return (
    <figure
      className={fs ? "fixed inset-0 z-[200] bg-ink overflow-y-auto px-4 py-16 sm:px-10" : "my-10"}
      data-testid="imagemap"
      data-fullscreen={fs ? "true" : undefined}
    >
      {fs && (
        <button
          onClick={toggleFs}
          className="fixed top-5 right-5 z-[210] inline-flex items-center gap-2 bg-ink-card border border-gold/40 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-gold hover:border-gold transition-colors"
          data-testid="imagemap-fullscreen-close"
        >
          <X className="w-3.5 h-3.5" /> Fermer
        </button>
      )}
      {fs && (
        <div className="fixed top-5 left-5 z-[210] flex items-center gap-2" data-testid="imagemap-zoom-bar">
          <button
            onClick={() => setView((v) => clampView({ ...v, scale: v.scale / 1.4 }))}
            className="inline-flex items-center justify-center w-9 h-9 bg-ink-card border border-gold/40 text-gold hover:border-gold transition-colors"
            aria-label="Zoom arrière"
            data-testid="imagemap-zoom-out"
          >
            <ZoomOut className="w-4 h-4" />
          </button>
          <button
            onClick={() => setView((v) => clampView({ ...v, scale: v.scale * 1.4 }))}
            className="inline-flex items-center justify-center w-9 h-9 bg-ink-card border border-gold/40 text-gold hover:border-gold transition-colors"
            aria-label="Zoom avant"
            data-testid="imagemap-zoom-in"
          >
            <ZoomIn className="w-4 h-4" />
          </button>
          {view.scale > 1 && (
            <button
              onClick={() => setView({ scale: 1, x: 0, y: 0 })}
              className="inline-flex items-center px-3 h-9 bg-ink-card border border-gold/40 font-mono text-[10px] uppercase tracking-[0.2em] text-gold hover:border-gold transition-colors"
              data-testid="imagemap-zoom-reset"
            >
              {Math.round(view.scale * 100)}% — Réinit.
            </button>
          )}
        </div>
      )}
      {hasLayers && (
        <div className="mb-3 hidden sm:flex items-center gap-3 flex-wrap" data-testid="imagemap-layer-bar">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">
            {currentTitle || "Vue"}
          </span>
          {currentLayer !== baseLayerId && (
            <button
              onClick={() => gotoLayer(baseLayerId)}
              className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#A0A5B1] hover:text-gold transition-colors underline-offset-4 hover:underline"
              data-testid="imagemap-back-home"
            >
              <Undo2 className="w-3.5 h-3.5" /> Revenir au début
            </button>
          )}
        </div>
      )}
      <div className={`hidden sm:block overflow-x-auto ${fs ? "max-w-[1200px] mx-auto" : ""}`} data-testid="imagemap-scroll">
        <div
          className={`relative border border-gold/25 bg-[#F5F0E6] select-none ${fs ? "min-w-[900px]" : "min-w-[640px]"}`}
          style={{
            touchAction: fs ? "none" : "pan-x pan-y",
            transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`,
            transformOrigin: "center center",
          }}
          onPointerDown={onPtrDown}
          onPointerMove={onPtrMove}
          onPointerUp={onPtrUp}
          onPointerCancel={onPtrUp}
          onClick={() => {
            if (moved.current) {
              moved.current = false;
              return;
            }
            setActive(null);
            setExpanded(false);
          }}
          data-testid="imagemap-frame"
        >
        <img
          src={currentImage}
          alt={currentTitle ? `Carte interactive — ${currentTitle}` : "Carte interactive"}
          className="w-full h-auto block"
          draggable={false}
          data-testid="imagemap-image"
        />
        <svg
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          className="absolute inset-0 w-full h-full"
        >
          {spots.map((s) => (
            <polygon
              key={s.id}
              points={s.points.map((p) => `${p.x},${p.y}`).join(" ")}
              data-testid={`imagemap-spot-${s.id}`}
              className="cursor-pointer fill-transparent stroke-transparent"
              style={{ vectorEffect: "non-scaling-stroke" }}
              strokeWidth="1.5"
              onMouseEnter={() => {
                cancelClose();
                openSpot(s.id);
              }}
              onMouseLeave={scheduleClose}
              onClick={(e) => {
                e.stopPropagation();
                if (moved.current) {
                  moved.current = false; // c'était un glisser/zoomer, pas un toucher
                  return;
                }
                // tactile : 1er toucher = infobulle, 2e toucher (ou bouton) = action
                if (isTouch && active !== s.id && (s.tooltip_html || s.title)) {
                  openSpot(s.id);
                  return;
                }
                if (s.goto !== null && s.goto !== undefined) {
                  gotoLayer(s.goto);
                  return;
                }
                if (s.link) {
                  navigate(s.link);
                  return;
                }
                if (isLong && !showFull) {
                  setExpanded(true); // tactile : le tap ouvre en grand
                  return;
                }
                setActive(active === s.id ? null : s.id);
                setExpanded(false);
              }}
            />
          ))}
        </svg>

        {activeSpot && (activeSpot.title || tipHtml) && (
          <div
            className={`absolute z-10 bg-ink border border-gold/40 shadow-2xl p-5 pointer-events-none ${
              showFull ? "w-[26rem] max-w-[92%]" : "w-72 max-w-[80%]"
            }`}
            style={tipStyle}
            data-testid="imagemap-tooltip"
            onMouseEnter={() => {
              cancelClose();
              setActive(activeSpot.id);
            }}
            onMouseLeave={scheduleClose}
          >
            {activeSpot.title && (
              <p className="font-serif text-gold text-base leading-snug">{activeSpot.title}</p>
            )}
            {tipHtml && (
              <div className="relative">
                <div
                  className={`imagemap-tip mt-2 text-xs text-[#A0A5B1] leading-relaxed pointer-events-auto ${
                    showFull ? "max-h-96 overflow-y-auto" : isLong ? "max-h-36 overflow-hidden" : "max-h-64 overflow-y-auto"
                  }`}
                  dangerouslySetInnerHTML={{ __html: tipHtml }}
                />
                {isLong && !showFull && (
                  <div className="absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-ink to-transparent pointer-events-none" />
                )}
              </div>
            )}
            {isLong && !showFull && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setExpanded(true);
                }}
                className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-gold hover:text-gold-light transition-colors pointer-events-auto"
                data-testid="imagemap-read-more"
              >
                Lire la suite
              </button>
            )}
            {showFull && isLong && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setExpanded(false);
                }}
                className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#626773] hover:text-gold transition-colors pointer-events-auto"
                data-testid="imagemap-read-less"
              >
                Réduire
              </button>
            )}
            {activeSpot.goto !== null && activeSpot.goto !== undefined && !showFull && (
              <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-gold/60">
                Cliquez pour continuer
              </p>
            )}
            {activeSpot.link && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(activeSpot.link);
                }}
                className="mt-3 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-gold hover:text-gold-light transition-colors pointer-events-auto border border-gold/40 px-3 py-2"
                data-testid="imagemap-open-fiche"
              >
                Ouvrir la fiche →
              </button>
            )}
          </div>
        )}
      </div>
      </div>

      {/* Version mobile : la carte est masquée, seule la liste à volets est proposée (groupée par type) */}
      <div className="sm:hidden mt-4 border border-white/10" data-testid="imagemap-mobile-list">
        {(() => {
          const zones = spots.filter((s) => s.tooltip_html || (s.goto !== null && s.goto !== undefined) || s.link);
          const order = data.groups || [];
          const grouped = order
            .map((g) => ({ label: g, zones: zones.filter((s) => s.group === g) }))
            .filter((g) => g.zones.length > 0);
          const reste = zones.filter((s) => !s.group || !order.includes(s.group));
          if (reste.length || !grouped.length) grouped.push({ label: null, zones: grouped.length ? reste : zones });
          return grouped.map((g, gi) => (
            <div key={g.label || "zones"} className={gi > 0 ? "border-t border-gold/20" : ""} data-testid={`imagemap-mobile-group-${gi}`}>
              <p className="px-4 pt-4 pb-2 font-mono text-[10px] uppercase tracking-[0.25em] text-gold" data-testid={`imagemap-mobile-group-label-${gi}`}>
                {g.label || "Explorer les zones"}
              </p>
              <div className="divide-y divide-white/5">
                {g.zones.map((s) => (
          <div key={s.id}>
            <button
              onClick={() => {
                if (s.goto !== null && s.goto !== undefined) {
                  gotoLayer(s.goto);
                  return;
                }
                if (s.tooltip_html) {
                  setMobileOpen(mobileOpen === s.id ? null : s.id); // 1er toucher = aperçu
                  return;
                }
                if (s.link) {
                  navigate(s.link);
                  return;
                }
                setMobileOpen(mobileOpen === s.id ? null : s.id);
              }}
              className="w-full text-left px-4 py-3 text-sm text-[#FAF8F5] hover:text-gold transition-colors flex items-center justify-between gap-3"
              data-testid={`imagemap-mobile-zone-${s.id}`}
            >
              <span>{s.title || "Zone"}</span>
              <span className="text-gold/60 text-xs shrink-0">
                {s.goto !== null && s.goto !== undefined ? "→" : mobileOpen === s.id ? "−" : "+"}
              </span>
            </button>
            {mobileOpen === s.id && (s.tooltip_html || s.link) && (
              <div className="px-4 pb-4">
                {s.tooltip_html && (
                  <div
                    className="imagemap-tip text-xs text-[#A0A5B1] leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: s.tooltip_html }}
                    data-testid={`imagemap-mobile-content-${s.id}`}
                  />
                )}
                {s.link && (
                  <button
                    onClick={() => navigate(s.link)}
                    className="mt-3 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-gold hover:text-gold-light transition-colors border border-gold/40 px-3 py-2"
                    data-testid={`imagemap-mobile-open-${s.id}`}
                  >
                    Ouvrir la fiche →
                  </button>
                )}
              </div>
            )}
          </div>
                ))}
              </div>
            </div>
          ));
        })()}
      </div>
      {hasLayers && (
        <div className="mt-4 hidden sm:flex items-center gap-x-5 gap-y-2 flex-wrap" data-testid="imagemap-stepper">
          {layers.map((l, i) => (
            <button
              key={l.id}
              onClick={() => gotoLayer(l.id)}
              className={`font-mono text-[10px] uppercase tracking-[0.2em] transition-colors underline-offset-4 ${
                currentLayer === l.id
                  ? "text-gold underline decoration-gold/60"
                  : "text-[#A0A5B1] hover:text-gold hover:underline"
              }`}
              data-testid={`imagemap-step-${l.id}`}
            >
              {i + 1}. {l.title}
            </button>
          ))}
        </div>
      )}

      <figcaption className={`mt-3 hidden sm:flex items-center gap-2 text-[#626773] ${fs ? "max-w-[1200px] mx-auto" : ""}`}>
        <MousePointer2 className="w-3.5 h-3.5 text-gold/70" />
        <span className="font-mono text-[10px] uppercase tracking-[0.25em]">
          {isTouch
            ? "Carte interactive — touchez les zones ; en plein écran, pincez pour zoomer"
            : hasLayers
              ? "Carte interactive — survolez les zones, ou naviguez avec les étapes ci-dessous"
              : "Carte interactive — survolez ou touchez les zones"}
        </span>
        <button
          onClick={toggleFs}
          className="ml-auto inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-gold hover:text-gold-light transition-colors shrink-0"
          data-testid="imagemap-fullscreen-toggle"
        >
          {fs ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
          {fs ? "Quitter le plein écran" : "Plein écran"}
        </button>
      </figcaption>
    </figure>
  );
}
